import FormField from "@/components/common/FormField"
import MainHeading from "@/components/common/MainHeading"
import Layout from "@/components/layout/Layout"
import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { errorTypes } from "@/utility/validation"
import { Button } from "@/components/ui/button"
import Card from "@/components/common/Card"
import { ThrowError } from "@/api/callApi"
import ShiftAndTimeService from "@/services/ShiftAndTime/ShiftAndTime"
import type { shiftAndTime, IshiftAndTimeResponse } from "@/interface/shiftAndTime.interface"
import { showToastNotification } from "@/utility/helper"
import { RoutesLink } from "@/utility/RouteLinks"
import { useEffect, useState } from "react"
import moment from "moment"
import FullPageLoader from "@/components/common/FullPageLoader"

const AddShiftAndTime: React.FC = () => {
    const navigate = useNavigate()
    const { id } = useParams()
    const [isLoading, setIsLoading] = useState(false)
    const [isFetching, setIsFetching] = useState(false)
    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<shiftAndTime>({
        mode: "onChange"
    });

    // ── Helpers for time conversion using moment ──────────────────────────────
    const timeToMinutes = (timeStr?: string): string => {
        if (!timeStr) return "";
        return String(moment.duration(timeStr).asMinutes());
    };

    const minutesToTime = (minsStr?: string | number): string => {
        const mins = parseInt(String(minsStr), 10) || 0;
        return moment.utc(moment.duration(mins, 'minutes').asMilliseconds()).format("HH:mm:ss");
    };

    // ── Edit mode: pre-fill form ──────────────────────────────────────────────
    const handleGetShiftAndTimeById = async () => {
        if (!id) return;
        setIsFetching(true)
        try {
            const response = await ShiftAndTimeService?.getShiftAndTimeById(id);
            if (response?.status) {
                const data = response?.data;
                setValue("shiftName", data?.shiftName);
                setValue("shiftStartTime", data?.shiftStartTime);
                setValue("shiftEndTime", data?.shiftEndTime);
                setValue("breakStartTime", data?.breakStartTime);
                setValue("breakEndTime", data?.breakEndTime);
                setValue("checkInBufferTime", timeToMinutes(data?.checkInBufferTime));
                setValue("checkOutBufferTime", timeToMinutes(data?.checkOutBufferTime));
                setValue("type", data?.type);
                setValue("isActive", data?.isActive);
            } else {
                showToastNotification("danger", response?.message);
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsFetching(false)
        }

    }

    useEffect(() => {
        if (id) handleGetShiftAndTimeById();
    }, [id]);

    // ── Submit ────────────────────────────────────────────────────────────────
    const onSubmit = async (data: shiftAndTime) => {
        try {
            setIsLoading(true);
            const payload = {
                shiftName: data.shiftName,
                shiftStartTime: data.shiftStartTime,
                shiftEndTime: data.shiftEndTime,
                breakStartTime: data.breakStartTime,
                breakEndTime: data.breakEndTime,
                checkInBufferTime: minutesToTime(data.checkInBufferTime),
                checkOutBufferTime: minutesToTime(data.checkOutBufferTime),
                type: data.type,
            };
            let response: IshiftAndTimeResponse;
            if (id) {
                response = await ShiftAndTimeService?.updateShiftAndTime(payload as shiftAndTime, id);
            } else {
                response = await ShiftAndTimeService?.createShiftAndTime(payload as shiftAndTime);
            }
            if (response?.status) {
                showToastNotification("success", response?.message);
                navigate(RoutesLink.shiftAndTime);
            } else {
                showToastNotification("danger", response?.message);
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    }

    if (isFetching) {
        return (
            <Layout>
                <FullPageLoader />
            </Layout>
        )
    }

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title={id ? "Edit Shift and Time" : "Add Shift and Time"}
                    onClick={() => navigate(-1)}
                    isBackButtonVisible
                />
                <Card>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                            {/* Shift Name */}
                            <FormField
                                {...register("shiftName", { ...errorTypes.required('Shift Name'), ...errorTypes.name })}
                                type="text"
                                label="Shift Name"
                                minLength={3}
                                maxLength={30}
                                placeholder="e.g. General Shift"
                                error={errors.shiftName?.message}
                                required
                            />

                            {/* Type */}
                            <FormField
                                {...register("type", { ...errorTypes.required('Type') })}
                                type="select"
                                value={String(watch("type") ?? "")}
                                label="Type"
                                options={[
                                    { value: "select", label: "Select Type" },
                                    { value: "office", label: "Office" },
                                    { value: "field", label: "Field" },
                                ]}
                                error={errors.type?.message}
                                required
                            />

                            {/* Shift Start Time */}
                            <FormField
                                {...register("shiftStartTime", { ...errorTypes.required('Shift Start Time') })}
                                type="time"
                                label="Shift Start Time"
                                error={errors.shiftStartTime?.message}
                                required
                            />

                            {/* Shift End Time */}
                            <FormField
                                {...register("shiftEndTime", {
                                    ...errorTypes.required('Shift End Time'),
                                    validate: (value) => {
                                        const startTime = watch("shiftStartTime");
                                        if (!startTime || !value) return true;
                                        return value > startTime || "Shift End Time must be after Shift Start Time";
                                    }
                                })}
                                type="time"
                                label="Shift End Time"
                                error={errors.shiftEndTime?.message}
                                required
                            />

                            {/* Break Start Time */}
                            <FormField
                                {...register("breakStartTime", {
                                    ...errorTypes.required('Break Start Time'),
                                    validate: (value) => {
                                        const shiftStart = watch("shiftStartTime");
                                        const shiftEnd = watch("shiftEndTime");
                                        if (!shiftStart || !value) return true;
                                        if (value <= shiftStart) return "Break Start must be after Shift Start";
                                        if (shiftEnd && value >= shiftEnd) return "Break Start must be before Shift End";
                                        return true;
                                    }
                                })}
                                type="time"
                                label="Break Start Time"
                                error={errors.breakStartTime?.message}
                                required
                            />

                            {/* Break End Time */}
                            <FormField
                                {...register("breakEndTime", {
                                    ...errorTypes.required('Break End Time'),
                                    validate: (value) => {
                                        const breakStart = watch("breakStartTime");
                                        const shiftEnd = watch("shiftEndTime");
                                        if (!breakStart || !value) return true;
                                        if (value <= breakStart) return "Break End must be after Break Start";
                                        if (shiftEnd && value >= shiftEnd) return "Break End must be before Shift End";
                                        return true;
                                    }
                                })}
                                type="time"
                                label="Break End Time"
                                error={errors.breakEndTime?.message}
                                required
                            />

                            {/* Check-in Buffer (minutes) */}
                            <FormField
                                {...register("checkInBufferTime", { ...errorTypes.required('Check-in Buffer') })}
                                type="number"
                                label="Check-in Buffer (minutes)"
                                placeholder="e.g. 15"
                                error={errors.checkInBufferTime?.message}
                                required
                            />

                            {/* Check-out Buffer (minutes) */}
                            <FormField
                                {...register("checkOutBufferTime", { ...errorTypes.required('Check-out Buffer') })}
                                type="number"
                                label="Check-out Buffer (minutes)"
                                placeholder="e.g. 15"
                                error={errors.checkOutBufferTime?.message}
                                required
                            />

                        </div>

                        <div className="flex justify-end">
                            <Button disabled={isLoading} type="submit">
                                {isLoading ? "Submitting..." : "Submit"}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </Layout>
    )
}

export default AddShiftAndTime;